// DS2 Select: a react-select wrapper - options as objects or groups, isMulti, isClearable, isSearchable,
// getOptionLabel / getOptionValue... every react-select prop but styles and menuPosition. Styled through react-select's
// unstyled + classNames (Select.module.css); the menu is portaled to document.body with a fixed position (above a
// ModalDialog). isMulti: the chosen values as tags, the list stays open while choosing and checks the chosen rows. On
// phones (useIsMobileDevice) a native <select> instead. The default placeholder and the "no options" /
// "loading" texts come from the app's translations. Inside a FormField: <FormField label>{({ labelId, isInvalid, errorId }) => <Select
// inputId={labelId} isInvalid={isInvalid} aria-describedby={errorId} options />}</FormField>
import cx from "classnames";
import { createContext, use, type ChangeEvent, type Ref } from "react";
import ReactSelect, {
  components,
  type ActionMeta,
  type ClassNamesConfig,
  type DropdownIndicatorProps,
  type GroupBase,
  type InputProps,
  type MultiValueRemoveProps,
  type OnChangeValue,
  type OptionProps,
  type OptionsOrGroups,
  type Props as ReactSelectProps,
} from "react-select";
import { useIsMobileDevice } from "../../hooks/useIsMobileDevice";
import useI18n, { type Translation } from "../../hooks/useTranslations";
import en from "../../i18n/en";
import { CheckIcon, ChevronDownIcon, CrossIcon } from "./icons";
import { mergeRefs } from "./mergeRefs";
import { ModalDialogContext } from "./modalDialogContext";
import styles from "./Select.module.css";

function selectClassNames<T, U extends boolean, G extends GroupBase<T>>(
  isInvalid?: boolean,
): ClassNamesConfig<T, U, G> {
  return {
    container: () => styles.container,
    control: (s) =>
      cx(
        styles.control,
        (s.isFocused || s.menuIsOpen) && styles.focused,
        s.menuIsOpen && styles.open,
        isInvalid && styles.invalid,
        s.isDisabled && styles.disabled,
      ),
    valueContainer: (s) => cx(styles.value, s.isMulti && s.hasValue && styles.valueMulti),
    placeholder: () => styles.placeholder,
    multiValue: () => styles.tag,
    multiValueRemove: () => styles.tagRemove,
    indicatorsContainer: () => styles.indicators,
    clearIndicator: () => styles.clear,
    dropdownIndicator: () => styles.chevron,
    menu: () => styles.menu,
    groupHeading: () => styles.groupHeading,
    option: (s) =>
      cx(
        styles.option,
        s.isFocused && styles.active,
        s.isSelected && styles.selected,
        s.isDisabled && styles.optionDisabled,
      ),
    noOptionsMessage: () => styles.notice,
    loadingMessage: () => styles.notice,
  };
}

// the caller's classNames are added to ours, not swapped for them
function mergeClassNames<T, U extends boolean, G extends GroupBase<T>>(
  own: ClassNamesConfig<T, U, G>,
  extra?: ClassNamesConfig<T, U, G>,
) {
  if (!extra) return own;
  type Fn = (state: unknown) => string;
  const a = own as Record<string, Fn>,
    b = extra as Record<string, Fn | undefined>;
  const merged: Record<string, Fn> = { ...b } as Record<string, Fn>;
  for (const key in a) merged[key] = (state) => cx(a[key]?.(state), b[key]?.(state));
  return merged as ClassNamesConfig<T, U, G>;
}

// what the parts below need from the Select (react-select passes only its own props down)
const SelectContext = createContext<{ t: Translation["designSystem"]; describedBy?: string }>({ t: en.designSystem });

// react-select's input, also described by the FormField's hint / error
function Input<T, U extends boolean, G extends GroupBase<T>>(props: InputProps<T, U, G>) {
  const { describedBy } = use(SelectContext);
  const ids = [props["aria-describedby"], describedBy].filter(Boolean).join(" ");
  return <components.Input {...props} aria-describedby={ids || undefined} />;
}

// a tag's ×: the DS2 cross, its label translated
function MultiValueRemove<T, U extends boolean, G extends GroupBase<T>>(props: MultiValueRemoveProps<T, U, G>) {
  const { t } = use(SelectContext);
  const label = `${t.removeLabel} ${props.selectProps.getOptionLabel(props.data)}`;
  return (
    <components.MultiValueRemove {...props} innerProps={{ ...props.innerProps, "aria-label": label }}>
      <CrossIcon width="8" height="8" />
    </components.MultiValueRemove>
  );
}

// the chevron at the right of the field, turned up while the list is open
function DropdownIndicator<T, U extends boolean, G extends GroupBase<T>>(props: DropdownIndicatorProps<T, U, G>) {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon width="16" height="16" />
    </components.DropdownIndicator>
  );
}

// a row of the list, with a check when it is chosen
function Option<T, U extends boolean, G extends GroupBase<T>>(props: OptionProps<T, U, G>) {
  return (
    <components.Option {...props}>
      {props.children}
      {props.isSelected ? <CheckIcon className={styles.check} width="16" height="16" /> : null}
    </components.Option>
  );
}

function isOptionGroup<T, G extends GroupBase<T>>(item: T | G): item is G {
  return typeof item === "object" && item !== null && Array.isArray((item as G).options);
}

function flattenOptions<T, G extends GroupBase<T>>(options: OptionsOrGroups<T, G>): T[] {
  return options.flatMap((item) => (isOptionGroup<T, G>(item) ? item.options : [item]));
}

const defaultGetOptionLabel = (option: unknown) => String((option as { label?: unknown })?.label ?? "");
const defaultGetOptionValue = (option: unknown) => String((option as { value?: unknown })?.value ?? "");

type NativeSelectProps<T, U extends boolean, G extends GroupBase<T>> = {
  options: OptionsOrGroups<T, G>;
  value?: ReactSelectProps<T, U, G>["value"];
  defaultValue?: ReactSelectProps<T, U, G>["defaultValue"];
  onChange?: ReactSelectProps<T, U, G>["onChange"];
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  isMulti?: U;
  isDisabled?: boolean;
  isInvalid?: boolean;
  placeholder?: string;
  name?: string;
  inputId?: string;
  required?: boolean;
  className?: string;
  "aria-describedby"?: string;
  ref?: Ref<HTMLSelectElement>;
};

// the native <select> / <option>s rendered on phones instead of the dropdown
function NativeSelect<T, U extends boolean, G extends GroupBase<T>>({
  options,
  value,
  defaultValue,
  onChange,
  getOptionLabel = defaultGetOptionLabel,
  getOptionValue = defaultGetOptionValue,
  isMulti,
  isDisabled,
  isInvalid,
  placeholder,
  name,
  inputId,
  required,
  className,
  "aria-describedby": describedBy,
  ref,
}: NativeSelectProps<T, U, G>) {
  const flatOptions = flattenOptions<T, G>(options);
  const findOption = (raw: string) => flatOptions.find((option) => getOptionValue(option) === raw);

  const toStringValue = (v: T | readonly T[] | null | undefined): string | string[] => {
    if (v === null || v === undefined) return isMulti ? [] : "";
    return Array.isArray(v) ? v.map(getOptionValue) : getOptionValue(v as T);
  };

  const valueProps =
    value !== undefined
      ? { value: toStringValue(value as T | readonly T[] | null) }
      : { defaultValue: toStringValue(defaultValue as T | readonly T[] | null | undefined) };

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (!onChange) return;
    if (isMulti) {
      const selected = Array.from(event.target.selectedOptions)
        .map((option) => findOption(option.value))
        .filter((option): option is T => option !== undefined);
      onChange(selected as OnChangeValue<T, U>, { action: "select-option", option: undefined } as ActionMeta<T>);
      return;
    }
    const option = event.target.value ? findOption(event.target.value) : undefined;
    onChange(
      (option ?? null) as OnChangeValue<T, U>,
      { action: option ? "select-option" : "clear", option } as ActionMeta<T>,
    );
  };

  return (
    <span className={styles.native}>
      <select
        ref={ref}
        className={cx(styles.select, isInvalid && styles.invalid, className)}
        multiple={isMulti}
        disabled={isDisabled}
        name={name}
        id={inputId}
        required={required}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        onChange={handleChange}
        {...valueProps}
      >
        {!isMulti && <option value="">{placeholder ?? ""}</option>}
        {options.map((item, index) =>
          isOptionGroup<T, G>(item) ? (
            <optgroup key={item.label ?? index} label={item.label}>
              {item.options.map((option) => (
                <option key={getOptionValue(option)} value={getOptionValue(option)}>
                  {getOptionLabel(option)}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={getOptionValue(item)} value={getOptionValue(item)}>
              {getOptionLabel(item)}
            </option>
          ),
        )}
      </select>
      <ChevronDownIcon className={styles.nativeChevron} width="16" height="16" />
    </span>
  );
}

export type SelectProps<T, U extends boolean = false, G extends GroupBase<T> = GroupBase<T>> = Omit<
  ReactSelectProps<T, U, G>,
  "styles" | "menuPosition" | "placeholder"
> & {
  placeholder?: string;
  isInvalid?: boolean;
  // not react-select's: the FormField's hint / error, added to the input's own description
  "aria-describedby"?: string;
  // what can take the focus: react-select's instance, or the native <select> on a phone
  ref?: Ref<{ focus: () => void }>;
};

export function Select<T, U extends boolean = false, G extends GroupBase<T> = GroupBase<T>>({
  isInvalid,
  placeholder,
  className,
  "aria-describedby": describedBy,
  ref,
  ...restProps
}: SelectProps<T, U, G>) {
  const t = useI18n().translation.designSystem;
  const resolvedPlaceholder = placeholder ?? t.selectPlaceholder;
  const isMobile = useIsMobileDevice();
  const dialog = use(ModalDialogContext);

  if (isMobile) {
    return (
      <NativeSelect<T, U, G>
        options={restProps.options ?? []}
        value={restProps.value}
        defaultValue={restProps.defaultValue}
        onChange={restProps.onChange}
        getOptionLabel={restProps.getOptionLabel as ((option: T) => string) | undefined}
        getOptionValue={restProps.getOptionValue as ((option: T) => string) | undefined}
        isMulti={restProps.isMulti}
        isDisabled={restProps.isDisabled}
        isInvalid={isInvalid}
        placeholder={resolvedPlaceholder}
        name={restProps.name}
        inputId={restProps.inputId ?? restProps.id}
        required={restProps.required}
        className={className}
        aria-describedby={describedBy}
        ref={mergeRefs(ref)}
      />
    );
  }

  return (
    <SelectContext value={{ t, describedBy }}>
      <ReactSelect<T, U, G>
        ref={mergeRefs(ref)}
        unstyled
        aria-invalid={isInvalid || undefined}
        noOptionsMessage={() => t.noOptionsLabel}
        loadingMessage={() => t.loadingLabel}
        closeMenuOnSelect={!restProps.isMulti}
        hideSelectedOptions={false}
        isClearable={false}
        {...restProps}
        className={className}
        placeholder={resolvedPlaceholder}
        classNames={mergeClassNames(selectClassNames<T, U, G>(isInvalid), restProps.classNames)}
        components={{
          IndicatorSeparator: null,
          DropdownIndicator,
          Option,
          Input,
          MultiValueRemove,
          ...restProps.components,
        }}
        // above the page; in a ModalDialog, inside its <dialog> (the top layer, above any z-index)
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
        menuPosition="fixed"
        menuPortalTarget={dialog ?? document.body}
      />
    </SelectContext>
  );
}
